import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api, { endpoints } from '../../utils/apiClient';

const AdminQuests = () => {
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingQuest, setEditingQuest] = useState(null);

  // Form state for new quest
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'easy',
    reward: {
      currency: 'BAI',
      amount: 10
    },
    image: '',
    gameUrl: '',
    participantLimit: 100,
    agentDisplayName: ''
  });

  // Fetch all quests on component mount
  useEffect(() => {
    fetchQuests();
  }, []);

  const fetchQuests = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.admin.quests.list);
      setQuests(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Error fetching quests:', error);
      toast.error('Failed to fetch quests');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      difficulty: 'easy',
      reward: {
        currency: 'BAI',
        amount: 10
      },
      image: '',
      gameUrl: '',
      participantLimit: 100,
      agentDisplayName: ''
    });
    setEditingQuest(null);
  };

  const handleEditClick = (quest) => {
    setEditingQuest(quest);
    setFormData({
      title: quest.title || '',
      description: quest.description || '',
      difficulty: quest.difficulty || 'easy',
      reward: {
        currency: quest.reward?.currency || 'BAI',
        amount: quest.reward?.amount || 10
      },
      image: quest.image || '',
      gameUrl: quest.gameUrl || '',
      participantLimit: quest.participantLimit || 100,
      status: quest.status || 'active',
      agentDisplayName: quest.agentDisplayName || ''
    });
    setShowCreateModal(true);
  };

  const handleDeleteClick = async (questId) => {
    if (!window.confirm('Are you sure you want to delete this quest?')) {
      return;
    }

    try {
      await api.delete(endpoints.admin.quests.delete(questId));
      toast.success('Quest deleted successfully');
      fetchQuests();
    } catch (error) {
      console.error('Error deleting quest:', error);
      toast.error('Failed to delete quest');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Handle numeric values
      const submitData = {
        ...formData,
        reward: {
          ...formData.reward,
          amount: Number(formData.reward.amount)
        },
        participantLimit: Number(formData.participantLimit)
      };

      if (editingQuest) {
        // Update existing quest
        await api.put(endpoints.admin.quests.update(editingQuest._id), submitData);
        toast.success('Quest updated successfully');
      } else {
        // Create new quest
        await api.post(endpoints.admin.quests.create, submitData);
        toast.success('Quest created successfully');
      }

      resetForm();
      setShowCreateModal(false);
      fetchQuests();
    } catch (error) {
      console.error('Error saving quest:', error);
      toast.error('Failed to save quest');
    } finally {
      setLoading(false);
    }
  };

  // Filter quests based on search term
  const filteredQuests = quests.filter(quest =>
    quest.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quest.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">Quest Management</h2>
        <div className="flex space-x-2">
          <div className="relative mr-4">
            <input
              type="text"
              placeholder="Search quests..."
              className="px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className="w-5 h-5 absolute right-3 top-3 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              ></path>
            </svg>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
          >
            Create Quest
          </button>
        </div>
      </div>

      {loading && quests.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-gray-800 rounded-lg overflow-hidden">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Difficulty
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Reward
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Participants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredQuests.length > 0 ? (
                filteredQuests.map((quest) => (
                  <tr key={quest._id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={quest.image || 'https://placehold.co/200x200'}
                            alt={quest.title}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{quest.title}</div>
                          <div className="text-sm text-gray-400 max-w-xs truncate">{quest.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        quest.isAIQuest ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {quest.isAIQuest ? 'AI Quest' : 'Standard Quest'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        quest.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                        quest.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {quest.difficulty?.charAt(0).toUpperCase() + quest.difficulty?.slice(1) || 'Easy'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {quest.reward?.amount} {quest.reward?.currency}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        quest.status === 'active' ? 'bg-green-100 text-green-800' :
                        quest.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {quest.status?.charAt(0).toUpperCase() + quest.status?.slice(1) || 'Active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {quest.participants?.length || 0} / {quest.participantLimit || 100}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditClick(quest)}
                        className="text-indigo-400 hover:text-indigo-300 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(quest._id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-gray-400">
                    {searchTerm ? 'No quests found matching your search.' : 'No quests found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Quest Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-gray-800 p-6 rounded-lg w-full max-w-2xl">
            <h3 className="text-xl font-semibold mb-4">
              {editingQuest ? 'Edit Quest' : 'Create New Quest'}
            </h3>
            <form onSubmit={(e) => handleSubmit(e)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  ></textarea>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Difficulty
                  </label>
                  <select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Participant Limit
                  </label>
                  <input
                    type="number"
                    name="participantLimit"
                    value={formData.participantLimit}
                    onChange={handleInputChange}
                    min="1"
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Reward Currency
                  </label>
                  <select
                    name="reward.currency"
                    value={formData.reward.currency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="BAI">BAI</option>
                    <option value="BITP">BITP</option>
                    <option value="USDT">USDT</option>
                    <option value="IMX">IMX</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Reward Amount
                  </label>
                  <input
                    type="number"
                    name="reward.amount"
                    value={formData.reward.amount}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Image URL
                  </label>
                  <input
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleInputChange}
                    placeholder="https://example.com/image.jpg"
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Game URL
                  </label>
                  <input
                    type="text"
                    name="gameUrl"
                    value={formData.gameUrl}
                    onChange={handleInputChange}
                    placeholder="/bitpool/index.html"
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Agent Display Name
                  </label>
                  <input
                    type="text"
                    name="agentDisplayName"
                    value={formData.agentDisplayName}
                    onChange={handleInputChange}
                    placeholder="e.g. Brad, Reaper, Melissa"
                    className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Name displayed in the game for this AI agent</p>
                </div>

                {editingQuest && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200"
                >
                  {loading ? 'Saving...' : editingQuest ? 'Update Quest' : 'Create Quest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

export default AdminQuests;
